import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconRefresh, IconX } from '@tabler/icons-react';

/**
 * UpdateBanner component
 *
 * Displays a small, discrete notification when a new version of the app is available.
 * Styled like Supabase's toast notifications - bottom-right corner, compact design.
 * Listens to the 'plugin_web_update_notice' event emitted by @plugin-web-update-notification/vite
 *
 * Features:
 * - Small toast notification in bottom-right corner
 * - "Refresh Now" button to reload the page
 * - "Later" button to dismiss the notification
 * - Fully translated (supports en, es, fr, sv)
 * - Slide-up animation
 * - Light green theme matching app colors
 */
export function UpdateBanner() {
  const { t } = useTranslation();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const handleUpdate = () => {
      setUpdateAvailable(true);
    };

    // Listen for update notification from plugin
    document.addEventListener('plugin_web_update_notice', handleUpdate);

    return () => {
      document.removeEventListener('plugin_web_update_notice', handleUpdate);
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    setUpdateAvailable(false);
  };

  if (!updateAvailable) return null;

  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-50 animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-green-600 text-white rounded-lg shadow-lg p-3 flex items-center gap-3">
        <IconRefresh className="w-5 h-5 flex-shrink-0 animate-spin" style={{ animationDuration: '3s' }} />

        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-tight">
            {t('system.update_available.title')}
          </p>
        </div>

        <div className="flex gap-1 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="p-1.5 hover:bg-green-700 rounded transition-colors"
            aria-label={t('system.update_available.dismiss')}
            title={t('system.update_available.dismiss')}
          >
            <IconX className="w-4 h-4" />
          </button>
          <button
            onClick={handleRefresh}
            className="px-3 py-1.5 text-xs font-medium bg-white text-green-700 hover:bg-gray-100 rounded transition-colors"
            aria-label={t('system.update_available.refresh')}
          >
            {t('system.update_available.refresh')}
          </button>
        </div>
      </div>
    </div>
  );
}
